import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppState } from "store/reducers/Reducer";
import { PATH_DASHBOARD } from "config";
import { useUserCatalog } from "hooks/useSession";
import { invitationStorage } from "utils/invitationUtils";
import { getValidatedRedirectUrl } from "utils/redirectUtils";
import { buildMcpConsentPath, mcpAuthorizationStorage } from "utils/mcpAuthorizationStorage";
import RouteFallback from "routes/RouteFallback";
import { NetworkError } from "components/common/NetworkError/NetworkError";
import { useOptionalUserPreferences } from "providers/user-preferences";
import { pickLandingWorkspace } from "utils/workspace/landingWorkspace";

interface RedirectHandlerProps {
  children: React.ReactNode;
}

/**
 * Route guard component that handles redirect parameter after authentication
 */
export const RedirectHandler: React.FC<RedirectHandlerProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useSelector((state: AppState) => state.auth.loggedIn);

  // Only load workspace logic when on dashboard root to avoid unnecessary queries
  const isOnDashboardRoot = location.pathname === PATH_DASHBOARD.dashboard;
  const redirectUrl = useMemo(() => getValidatedRedirectUrl(location.search), [location.search]);
  const pendingInvitationToken = invitationStorage.getPendingInvitationToken?.() ?? null;

  const {
    data: catalog,
    isLoading: isCatalogLoading,
    isFetching: isCatalogFetching,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useUserCatalog();
  const requiresCatalog = loggedIn && isOnDashboardRoot && !redirectUrl && !pendingInvitationToken;

  // Saved by useWorkspaceIdentifiers only when the user opens a workspace URL.
  const preferencesContext = useOptionalUserPreferences();
  const lastOpenedWorkspaceId = preferencesContext?.preferences.navigation?.lastWorkspaceId;
  // Without a cached value, wait for the server copy so a new device lands right too.
  const isLastOpenedKnown =
    Boolean(lastOpenedWorkspaceId) || (preferencesContext?.isHydrated ?? true);

  const landingWorkspace = useMemo(
    () => pickLandingWorkspace(catalog?.workspaces ?? [], lastOpenedWorkspaceId),
    [catalog?.workspaces, lastOpenedWorkspaceId]
  );

  useEffect(() => {
    // Only handle redirects for authenticated users
    if (!loggedIn) return;

    // 1) Handle explicit redirect param (safe local paths only)
    if (redirectUrl) {
      navigate(redirectUrl, { replace: true });
      return;
    }

    // 1a) An AI app's authorization request saved before sign-in: finish consent
    const pendingMcpAuthorization = mcpAuthorizationStorage.get();
    if (pendingMcpAuthorization) {
      navigate(buildMcpConsentPath(pendingMcpAuthorization), { replace: true });
      return;
    }

    // 1b) Handle pending invitation token stored pre-auth: send to join flow
    if (pendingInvitationToken) {
      const joinUrl = PATH_DASHBOARD.joinWorkspace.replace(":token", pendingInvitationToken);
      navigate(joinUrl, { replace: true });
      // Do not clear immediately to avoid races; the join page will clear after success
      return;
    }

    // 2) Default root redirect: always redirect to projects page first
    if (isOnDashboardRoot) {
      if (isCatalogLoading || isCatalogFetching || isCatalogError || !catalog) return;
      if (!isLastOpenedKnown) return;

      if (landingWorkspace) {
        navigate(`/workspaces/${landingWorkspace.workspace_id}/projects`, { replace: true });
      }
    }
  }, [
    loggedIn,
    redirectUrl,
    pendingInvitationToken,
    isOnDashboardRoot,
    isCatalogLoading,
    isCatalogFetching,
    isCatalogError,
    catalog,
    isLastOpenedKnown,
    landingWorkspace,
    navigate,
  ]);

  if (requiresCatalog && isCatalogError) {
    return (
      <div className="min-h-screen bg-card flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <NetworkError
            title="Couldn't load your workspaces"
            description="We couldn't load your workspace list. Check your connection, then try again."
            onRetry={() => {
              void refetchCatalog();
            }}
          />
        </div>
      </div>
    );
  }

  if (requiresCatalog && (isCatalogLoading || isCatalogFetching || !catalog)) {
    return <RouteFallback containerClassName="min-h-screen" />;
  }

  return <>{children}</>;
};
